import React from 'react';
import {Button, Slider, Tooltip} from 'antd';
import cx from 'classnames';
import {UserStore} from '@/stores';
import StarSvg from '@/assets/billing/star.svg';
import HelpSvg from '@/assets/billing/help.svg';
import type {IBillingOverviewView} from '@/stores/BillingStore';
import {formatBackendTime, formatNumberWithCommas, getDuMatePurchaseUrl, getSubscriptionPlanDesc} from '@/utils';
import styles from './index.module.less';

interface IBillingOverviewCardProps {
    overview: IBillingOverviewView;
    isEnterprise: boolean;
    isEnterpriseAdmin: boolean;
    isEnterpriseSub: boolean;
    subscriptionPlan: string;
    userStore: UserStore;
    onPurchase: () => void;
    onManageSubscription: () => void;
    onEnableAutoRenew: () => void;
    onGoToEnterpriseAdmin: () => void;
}

interface ICreditMetricProps {
    label: string;
    value?: number;
    desc?: string;
    tooltip?: React.ReactNode;
}

interface ISubscriptionExpireProps {
    overview: IBillingOverviewView;
    subscriptionPlan: string;
    onEnableAutoRenew: () => void;
}

interface IActionGroupProps {
    isEnterprise: boolean;
    isEnterpriseAdmin: boolean;
    subscriptionPlan: string;
    onPurchase: () => void;
    onManageSubscription: () => void;
    onGoToEnterpriseAdmin: () => void;
}

interface IMetricGridProps {
    overview: IBillingOverviewView;
    isEnterprise: boolean;
    isEnterpriseAdmin: boolean;
    isEnterpriseSub: boolean;
    userStore: UserStore;
}

function formatExpireDesc(points: number, expireAt: string) {
    if (!points || !expireAt) {
        return '';
    }

    return `${points}资源点将于 ${formatBackendTime(expireAt)} 到期`;
}

const tooltipTitle = (
    <div className={styles.helpWrapper}>
        <div>资源点获取规则</div>
        <div>增购资源点：购买用量包后获得，有对应有效期，到期失效。</div>
        <div>赠送资源点：来自活动奖励，无有效期限制。</div>
    </div>
);

const CreditMetric: React.FC<ICreditMetricProps> = ({label, value = 0, desc, tooltip}) => (
    <div className={styles.metricItem}>
        <div className={styles.metricMeta}>
            <span className={styles.metricLabel}>
                {label}
                {tooltip}
            </span>
            {desc && <span className={styles.metricDesc}>{desc}</span>}
        </div>
        <span className={styles.metricValue}>{formatNumberWithCommas(value)}</span>
    </div>
);

const SubscriptionExpire: React.FC<ISubscriptionExpireProps> = ({overview, subscriptionPlan, onEnableAutoRenew}) => {
    const expireText = getSubscriptionPlanDesc(subscriptionPlan, overview.expireText) || '-';
    const enableAutoRenewText = overview.enableAutoRenewButtonText || '开启连续包月';

    return (
        <span className={styles.expireText}>
            {expireText}
            {overview.showEnableAutoRenewButton && (
                <div className={styles.enableAutoRenewBtn} onClick={onEnableAutoRenew}>
                    {enableAutoRenewText}
                </div>
            )}
        </span>
    );
};

const ActionGroup: React.FC<IActionGroupProps> = props => {
    const {
        isEnterprise, isEnterpriseAdmin, subscriptionPlan,
        onPurchase, onManageSubscription, onGoToEnterpriseAdmin
    } = props;
    const manageButtonText = subscriptionPlan === 'Pro' ? '管理订阅' : '升级';

    // 企业用户：增购资源点跳转 DuMate 购买页（与 fm-agent-fe 对齐）；个人用户打开购买弹窗
    const handlePurchaseClick = () => {
        if (isEnterprise) {
            window.open(getDuMatePurchaseUrl('credits'), '_blank', 'noopener,noreferrer');
            return;
        }
        onPurchase();
    };

    return (
        <div className={styles.actionGroup}>
            <Button className={styles.purchaseBtn} onClick={handlePurchaseClick}>增购资源点</Button>
            {!isEnterprise && (
                <Button className={styles.manageBtn} type="primary" onClick={onManageSubscription}>
                    {manageButtonText}
                </Button>
            )}
            {isEnterpriseAdmin && (
                <Button className={styles.enterpriseAdminBtn} onClick={onGoToEnterpriseAdmin}>
                    前往企业后台
                </Button>
            )}
        </div>
    );
};

const MetricGrid: React.FC<IMetricGridProps> = ({userStore, overview, isEnterprise, isEnterpriseSub}) => {
    if (isEnterpriseSub) {
        const quota = overview.subUserCreditQuota ?? userStore.subUserCreditQuota ?? 0;
        const remaining = overview.subUserCreditRemaining ?? userStore.subUserCreditRemaining ?? 0;
        const motherAvailable = overview.subUserMotherAvailable;
        const refreshTime = overview.subUserCreditRefreshTime;
        // 状态优先级：无上限(quota=-1) > 个人额度用尽(remaining=0) > 企业池不足(母池可用<=本人剩余) > 正常
        const unlimited = quota === -1;
        const personalEmpty = !unlimited && remaining <= 0;
        const enterpriseEmpty = !unlimited && !personalEmpty
            && motherAvailable != null && motherAvailable <= remaining;
        const progressPct = unlimited ? 100 : (quota > 0 ? Math.min(100, (remaining / quota) * 100) : 0);

        return (
            <div className={styles.subCreditBlock}>
                <span className={styles.subCreditLabel}>可用资源点</span>
                <div className={styles.subCreditRow}>
                    {unlimited ? (
                        <span className={styles.subCreditUnlimited}>以企业资源点额度为上限</span>
                    ) : (
                        <div className={styles.subCreditNumbers}>
                            <span className={cx(styles.subCreditRemaining, {[styles.subCreditMuted]: enterpriseEmpty})}>
                                {formatNumberWithCommas(remaining)}
                            </span>
                            <span className={styles.subCreditQuota}>/ {formatNumberWithCommas(quota)}</span>
                        </div>
                    )}
                    {personalEmpty ? (
                        <span className={styles.subCreditWarn}>资源点不足，请联系管理员</span>
                    ) : enterpriseEmpty ? (
                        <span className={styles.subCreditWarn}>企业资源点不足，请联系管理员</span>
                    ) : refreshTime ? (
                        <span className={styles.subCreditExpire}>{formatBackendTime(refreshTime)} 重置</span>
                    ) : null}
                </div>
                <Slider
                    className={cx(styles.subCreditSlider, {[styles.subCreditSliderMuted]: enterpriseEmpty})}
                    value={progressPct}
                    min={0}
                    max={100}
                    disabled
                    tooltip={{open: false}}
                />
            </div>
        );
    }

    return (
        <div className={styles.metricGrid}>
            <CreditMetric
                label="资源点"
                value={overview.dailyCredit}
                tooltip={(
                    <Tooltip
                        classNames={{root: styles.customTooltip}}
                        title={tooltipTitle}
                        trigger="click"
                        placement="top"
                    >
                        <span className={styles.sparkTrigger} role="button" tabIndex={0}>
                            <HelpSvg />
                        </span>
                    </Tooltip>
                )}
            />
            {isEnterprise ? (
                <CreditMetric
                    label="订阅（可分配）"
                    value={overview.enterpriseAllocatableCredits}
                />
            ) : (
                <CreditMetric
                    label="登录资源点"
                    value={overview.subscriptionPoints}
                    desc={overview.subscriptionRefreshTime}
                />
            )}
            {isEnterprise ? (
                <CreditMetric
                    label="通用"
                    value={overview.generalCredits}
                    desc={formatExpireDesc(overview.generalNextExpireRemain ?? 0, overview.generalNextExpireAt ?? '')}
                />
            ) : (
                <CreditMetric
                    label="增购资源点"
                    value={overview.purchasedPoints}
                    desc={formatExpireDesc(overview.purchasedNextExpireRemain, overview.purchasedNextExpireAt)}
                />
            )}
            {isEnterprise ? (
                <CreditMetric
                    label="结转"
                    value={overview.carryoverCredits}
                    desc={formatExpireDesc(overview.carryoverNextExpireRemain ?? 0, overview.carryoverNextExpireAt ?? '')}
                />
            ) : (
                <CreditMetric
                    label="赠送资源点"
                    value={overview.giftedPoints}
                    desc={formatExpireDesc(overview.giftedNextExpireRemain, overview.giftedNextExpireAt)}
                />
            )}
        </div>
    );
};

const BillingOverviewCard: React.FC<IBillingOverviewCardProps> = props => {
    const {
        overview, isEnterprise, isEnterpriseAdmin, isEnterpriseSub, subscriptionPlan,
        onPurchase, onManageSubscription, onEnableAutoRenew, onGoToEnterpriseAdmin
    } = props;
    const planTitle = props.userStore.enterprisePlanText;

    return (
        <section className={styles.summaryCard}>
            <header className={styles.summaryHeader}>
                <div className={styles.summaryMeta}>
                    <div className={styles.planRow}>
                        <span className={styles.planTitle}>{planTitle}</span>
                        <StarSvg />
                    </div>
                    {isEnterpriseSub && (
                        <span className={styles.expireText}>由管理员统一管理、分配席位与资源点</span>
                    )}
                    {!isEnterprise && !isEnterpriseSub && (
                        <SubscriptionExpire
                            overview={overview}
                            subscriptionPlan={subscriptionPlan}
                            onEnableAutoRenew={onEnableAutoRenew}
                        />
                    )}
                </div>

                {!isEnterpriseSub && (
                    <ActionGroup
                        isEnterprise={isEnterprise}
                        isEnterpriseAdmin={isEnterpriseAdmin}
                        subscriptionPlan={subscriptionPlan}
                        onPurchase={onPurchase}
                        onManageSubscription={onManageSubscription}
                        onGoToEnterpriseAdmin={onGoToEnterpriseAdmin}
                    />
                )}
            </header>

            <MetricGrid
                overview={overview}
                isEnterprise={isEnterprise}
                isEnterpriseAdmin={isEnterpriseAdmin}
                isEnterpriseSub={isEnterpriseSub}
                userStore={props.userStore}
            />
        </section>
    );
};

export default BillingOverviewCard;
